import { Socket } from "socket.io-client"
import { ModalityDataSource } from "../Types/ModalityDataSource"
import { PageType } from "../Types/PageType"
import { DecodedPixels, SocketResponse } from "../Types/SocketResponse"
import { decode } from "@msgpack/msgpack"
import { TraceData } from "./TimeSeriesData"
import { OrderedBuffer } from "./OrderedBuffer"
import { TimeSeriesPageManager } from "./TimeSeriesPageManager"

var pageId = 0

export type ModalityPageProperties = {
	socketId: string
	startTime: number
	endTime: number
	width: number
	patientId: string
	modalityDataSources: ModalityDataSource[]
	patientIsAdmitted: boolean
	maxReadTime: number | undefined
	getDataQuerySocket: null | ((id?: string) => Socket)
}

type SingleModalityEdges = [number | undefined, number][]
type MultipleModalityEdges = [number | undefined, number, number][]
type DecodedTimeSeriesData = {
	times: number[]
	data: number[] | (number | null)[][]
}

type CachedRender = {
	bitmap: ImageBitmap
	dirty: boolean // If we need to completely redraw using the original data
	edges: SingleModalityEdges | MultipleModalityEdges
}

export type TimingInformation = {
	gapIndexes: number[]
	samplingPeriod: number | null
}

export abstract class Page<DataType, PageProperties extends ModalityPageProperties = ModalityPageProperties> {
	public socketId: string = "?"
	public id: number
	public index: number = 0
	public startTime: number = 0
	public endTime: number = 0
	public width: number = 0
	public patientId: string = "?"
	public modalityDataSources: ModalityDataSource[] = []
	public maxReadTime: number | undefined = undefined // The maximum timestamp we are allowed to read from the file. Useful for live review and analysis cache invalidation.
	public needsAutoScale: boolean = true

	// TODO: Maybe these all belong in their own big object that can be accessed at once, ModalityMetadata or something like that.
	public data = new Map<number, Map<string, DataType>>() // Data Object Id, then modality.
	public renderCache = new Map<number, Map<string, CachedRender>>() // Data Object Id, then modality.
	public timingInformation = new Map<number, Map<string, TimingInformation>>() // Data Object Id, then modality

	protected patientIsAdmitted: boolean = true
	protected enforceDataPointLimit: boolean = false
	protected getDataQuerySocket: null | ((id?: string) => Socket) = null

	// Data Object ID : ["ABP", "ICP", ...]
	// When working with multiple data sources, the same "modality" can be loaded from multiple sources.
	protected dataObjectsLoaded = new Map<number, Map<string, boolean>>()
	protected dataObjectsLoading = new Map<number, Map<string, boolean>>()

	// UTC timestamps of the latest read end time that we requested and received from the backend.
	// Used in live review to make sure we don't re-request the same intervals of data in partial page loading.
	private lastRequestedPartialLoadEndTime: number | null = null
	private widthOnRequest: number = 0 // Keeps track of the resolution that we requested the data, needed to reload when screen size changes.

		// When the page loads partially, call this function.
	protected onPartialLoad: ((page: Page<DataType>) => void) | null = null
	protected onFinishedLoading: ((page: Page<DataType>) => void) | null = null

	public hasNotReceivedFullDataRange: boolean = true
	public loadingMessage: string = "not loaded"
	public responseBuffers = new Map<string, OrderedBuffer<SocketResponse>>() // Asynchronous packets need to be sorted into the correct order on arrival.
	private validRequestIds: Set<string> = new Set() // If a request comes back and the ID is not in the set, then we ignore it.
	private pageManager: TimeSeriesPageManager<any, any>

	constructor(pageManager: TimeSeriesPageManager<any, any>) {
		this.id = pageId++
		this.pageManager = pageManager
	}
	
	abstract getType(): PageType

	get loaded() {
		return this.modalityDataSources.every(dataSource => (
			this.dataObjectsLoaded.get(dataSource.dataObjectId)?.get(this.getModalityDataSourceKey(dataSource)) === true
			&& this.data.get(dataSource.dataObjectId)?.get(dataSource.modality) !== undefined
			)
		)
	}

	get loading() {
		return this.modalityDataSources.some(dataSource => (
			this.dataObjectsLoading.get(dataSource.dataObjectId)?.get(this.getModalityDataSourceKey(dataSource)) === true)
		)
	}

	get finishedLoading() {
		for (const buffer of this.responseBuffers.values()) {
			if (!buffer.isCompleted()) {
				return false
			}
		}

		return true
	}

	public needsToBeLoaded() {
		if (this.loading || this.modalityDataSources.length === 0) {
			return false
		}

		return (
			!this.loaded 
			|| this.hasNotReceivedFullDataRange 
			|| (this.widthOnRequest > 0 && this.widthOnRequest !== this.width))
	}

	updateProperties(properties: PageProperties) {
		Object.assign(this, properties)
	}

	socketEventName = () => "render_modalities"

	requestData(batchedDataSources: Map<number, ModalityDataSource[]>, socket: Socket, readStartTime: number, readEndTime: number) {
		if (this.getDataQuerySocket === null) {
			return
		}

		const requestId = `${this.socketId}-${socket.id}-${Date.now()}-${Math.random()}-${Math.random()}` // Can't use crypto.randomUUID because of TS compilation failure.

		this.validRequestIds = new Set()
		

		this.modalityDataSources.forEach(modalityDataSource => {
			const key = modalityDataSource.modality
			const existingBuffer = this.responseBuffers.get(key)
			if (existingBuffer) {
				existingBuffer.clear()
			} else {
				this.responseBuffers.set(key, new OrderedBuffer())
			}
		})

		// The number of pixels that we request should be proportional to how much of the page this section takes up.
		let ratio = (readEndTime - readStartTime) / (this.endTime - this.startTime)
		
		// clamp it between 0 and 1
		ratio = Math.min(1, Math.max(0, ratio)) 

		// at least two pixels should be requested, even if the ratio is very small. Increase for more frequency resolution.
		// This allows us to (as performantly as possible) visualize high resolution data in real-time with a large viewing window.
		// If there are too many pixels, the page will be re-requested and downsampled by the backend.
		const width = Math.max(4, Math.ceil(ratio * this.width))

		batchedDataSources.forEach((dataSources, dataObjectId) => {
			socket.emit(
				this.socketEventName(), 
				this.patientId, 
				dataObjectId, 
				this.id, 
				dataSources, 
				readStartTime, 
				readEndTime,
				width, 
				this.patientIsAdmitted,
				requestId
			)
		})

		this.lastRequestedPartialLoadEndTime = readEndTime
		this.widthOnRequest = this.width
		this.validRequestIds.add(requestId)
	}

	receiveSocketResponse(response: SocketResponse, unsubscribe: () => void) {
		if (!this.validRequestIds.has(response.request_id)) {
			// Ignore any responses that we weren't expecting.
			return
		}

		// We have to buffer the responses because they can return out of order.
		let modalityResponseBuffer = this.responseBuffers.get(response.modality)

		if (modalityResponseBuffer === undefined) {
			console.warn(`${this.id} Response buffer should have been created before requesting the data`)
			return
		}

		modalityResponseBuffer.setMaxId(response.total)
		const readyResponses = modalityResponseBuffer.add(response.order, response)

		let isFinished = false
		let propagateBackward = false
		let propagateForward = false

		try {
			for (let i = 0; i < readyResponses.length; i++) {
				const readyResponse = readyResponses[i]

				if (readyResponse.error) {
					throw new Error(readyResponse.error)
				}

				const { dataObjectDataMap, timingInformationMap } = this.setUpDataStorage(readyResponse)
				const [data, compressedTimes] = decode(readyResponse.pixels) as DecodedPixels
				const dataLength = data.length > 0 && Array.isArray(data[0]) ? data[0].length : data.length
				const [times, knownGaps, knownSamplingPeriod] = this.extractTimes(compressedTimes, dataLength, modalityResponseBuffer)

				this.handleDataConversionInPlace(data, readyResponse.conversion_factor)

				const newData: DecodedTimeSeriesData = { data, times }
				const readyResponseDataKey = readyResponse.modality
				const oldData = dataObjectDataMap.get(readyResponseDataKey) as TraceData | undefined
				const traceData = this.combineTimeSeriesData(oldData, newData)

				if (this.enforceDataPointLimit && traceData.times.length > 4 * this.width) {
					throw new Error("too many pixels!")
				}

				dataObjectDataMap.set(readyResponseDataKey, traceData as unknown as DataType)
				this.updateTimingInformation(timingInformationMap, knownGaps, knownSamplingPeriod, readyResponseDataKey, traceData)

				// If we are loading data on the edges of the page, invalidate the cache of the previous and next page
				if (readyResponse.order === 1) {
					propagateBackward = true
				}

				if (readyResponse.order === readyResponse.total) {
					propagateForward = true
				}

				// Prevent re-rendering until we have processed all the buffered data we have.
				if (i === readyResponses.length - 1) {
					modalityResponseBuffer.updateUnloadedStartTime(readyResponse.chunk_end_time/1000)
					this.invalidateModalityRenderCache(readyResponseDataKey, propagateBackward, propagateForward)
					isFinished = this.updateLoadedState(readyResponse)
				}
			}

			if (isFinished) {
				unsubscribe()
			}

		} catch (e) {
			console.error(e)
			unsubscribe()
			this.unload()
		}
	}

	public unload = () => {
		this.validRequestIds = new Set()
		this.responseBuffers.clear()
		this.lastRequestedPartialLoadEndTime = null
		this.data.clear()
		this.dataObjectsLoaded.clear()
		this.dataObjectsLoading.clear()
		this.clearRenderCache()
	}

	public cancel = () => {
		const socket = this.getDataQuerySocket?.(this.socketId) // Make sure the cancel request gets sent on the same send buffer as the load request.
		socket?.emit("cancel_render_modalities", [...this.validRequestIds])
		this.unload()
	}

	public clearRenderCache = () => {
		this.renderCache.forEach(dataObjectCache => {
			dataObjectCache.forEach(cache => cache.bitmap.close())
			dataObjectCache.clear()
		})
		this.renderCache.clear()
	}

	public invalidateModalityRenderCache = (modality: string, propagateBackward: boolean, propagateForward: boolean) => {
		this.renderCache.forEach(dataObjectCache => {
			dataObjectCache.forEach((cache, key) => {
				if (key.includes(modality)) {
					cache.dirty = true
				}
			})
		})

		if (propagateBackward) {
			const prevPage = this.pageManager.getPreviousPage(this)
			prevPage?.invalidateModalityRenderCache(modality, false, false)
		}

		if (propagateForward) {
			const nextPage = this.pageManager.getNextPage(this)
			nextPage?.invalidateModalityRenderCache(modality, false, false)
		}
	}

	public updateRenderCache = (dataObjectId: number, key: string, newValue: CachedRender) => {
		if (!this.renderCache.has(dataObjectId)) {
			this.renderCache.set(dataObjectId, new Map())
		}

		const cachedRender = this.renderCache.get(dataObjectId)?.get(key)

		if (cachedRender) {
			cachedRender.bitmap.close()
		}

		this.renderCache.get(dataObjectId)?.set(key, newValue)
	}

	public load = () => {
		if (!this.needsToBeLoaded() || this.width <= 0 || this.getDataQuerySocket === null || (this.patientIsAdmitted && !this.maxReadTime)) {
			this.onFinishedLoading?.(this)
			return
		}
		
		if (this.widthOnRequest > 0 && this.widthOnRequest !== this.width) {
			// The current data is requested at the wrong resolution and must be re-requested.
			this.unload()
		}

		const [readStartTime, readEndTime] = this.getReadStartAndEndTime()

		if (readEndTime <= readStartTime) {
			this.onFinishedLoading?.(this)
			return
		}

		// Data Object ID: [{ modality: "ABP"}, { modality: "ICP" }, { modality: "PRx", onDemandAnalysis: {...} } ...]
		// Making one socket call with all of the modalities is more efficient than sending each one separately.
		// e.g. reading 7 modalities only requires setting everything up one time instead of 7 times.
		const batchedModalitiesByDataObjectId = new Map<number, ModalityDataSource[]>()

		// Prevent asking for identical data multiple times (e.g. different indexes of the same composite data, like MAPopt)
		const uniqueDataSources = Array.from(new Map(this.modalityDataSources.map(dataSource => [this.getModalityDataSourceKey(dataSource), dataSource])).values())

		const dataSourcesToLoad = uniqueDataSources
			.filter(dataSource => {
				const key = this.getModalityDataSourceKey(dataSource)
				return !this.dataObjectsLoaded.get(dataSource.dataObjectId)?.get(key) && !this.dataObjectsLoading.get(dataSource.dataObjectId)?.get(key)
			})

		const socketEventName = this.socketEventName()

		dataSourcesToLoad.forEach(dataSource => {
			const key = this.getModalityDataSourceKey(dataSource)
			// Add to the list of modalities
			const existingList = batchedModalitiesByDataObjectId.get(dataSource.dataObjectId) ?? []
			batchedModalitiesByDataObjectId.set(dataSource.dataObjectId, [...existingList, dataSource ])

			// Update the loading state
			const existingLoadingModalities = this.dataObjectsLoading.get(dataSource.dataObjectId) ?? new Map()
			this.dataObjectsLoading.set(dataSource.dataObjectId, existingLoadingModalities.set(key, true))
		})

		const socket = this.getDataQuerySocket(this.socketId)

		const allListeners: Array<(socketResponse: SocketResponse) => void> = []

		batchedModalitiesByDataObjectId.forEach((_, dataObjectId) => {
			const listener = (socketResponse: SocketResponse) => {
				if (socketResponse.page_id === this.id && socketResponse.data_object_id === dataObjectId) {
					this.receiveSocketResponse(socketResponse, () => socket.off(socketEventName, listener))
				}
			}

			allListeners.push(listener)
			socket.on(socketEventName, listener)
		})

		const cancelLoad = () => {
			allListeners.forEach(listener => socket.off(socketEventName, listener))
			this.dataObjectsLoading.forEach(modalityMap => {
				modalityMap.forEach((_, modality) => {
					modalityMap.set(modality, false)
				})
			})
		}

		try {
			this.requestData(batchedModalitiesByDataObjectId, socket, readStartTime, readEndTime)
		} catch (e) {
			console.error(e)
			cancelLoad()
		}
	}

	private getReadStartAndEndTime() {
		// Only request data that we haven't already asked for and received
		let readStartTime = this.startTime
		let readEndTime = this.endTime

		if (this.lastRequestedPartialLoadEndTime) {
			readStartTime = Math.max(this.startTime, this.lastRequestedPartialLoadEndTime) 
		}

		if (this.maxReadTime && this.maxReadTime < this.endTime) {
			if (this.patientIsAdmitted) {
				// In live-review mode, make sure the partial loads go out on a grid. 
				// We need to make sure that each request (2px) is at least the same amount of time.
				// Otherwise, we end up with requests for 500ms of data and requests for 3s of data both getting 2 pixels, which makes it look weird.
				const maxReadTimeOnGrid = Math.ceil(this.maxReadTime / 1000) * 1000
				readEndTime = Math.min(maxReadTimeOnGrid, this.endTime)
			} else {
				// Otherwise, make sure to limit by the max read time (file end time).
				readEndTime = Math.min(this.maxReadTime, this.endTime)
			}
		}

		return [readStartTime, readEndTime]
	}

	private getModalityDataSourceKey = (dataSource: ModalityDataSource) => `${dataSource.modality}-${dataSource.dataObjectId}`

	private getSocketResponseModalityDataSourceKey = (response: SocketResponse) => `${response.modality}-${response.data_object_id}`

	// Timestamps are compressed into continuous chunks of timestamps to save bits.
	// This function just de-compresses the timestamps back into a list of the actual timestamp values.
	private extractTimes(compressedTimes: number[][], dataLength: number, buffer: OrderedBuffer<SocketResponse>): [Array<number>, Array<number>, number] {
		const times = new Array<number>(dataLength).fill(0)
		let timesOffset = 0

		for (const [startTimeMicroseconds, endTimeMicroseconds, numberOfPoints] of compressedTimes) {
			if (startTimeMicroseconds === undefined || endTimeMicroseconds === undefined || numberOfPoints === undefined) {
				continue
			}

			const start = startTimeMicroseconds / 1e3
			const end = endTimeMicroseconds / 1e3

			buffer.addTimingRecord(start, end, numberOfPoints)

			if (numberOfPoints === 1) {
				times[timesOffset++] = end
				continue
			}
			
			const step = (end - start) / (numberOfPoints - 1)

			for (let i = 0; i < numberOfPoints; i++) {
				times[timesOffset++] = start + i * step
			}
		}

		return [times, buffer.knownGaps, buffer.knownSamplingPeriod]
	}

	// For data compression purposes, data can come over the network as integers, even if the data is floating point.
	// This offers a significant performance gain because we can use fewer bits to represent approximately the same number.
	// The conversion factor is the number that we multiply by to get the floating point number out of the integer.
	private handleDataConversionInPlace(data: number[] | (number | null)[][], conversionFactor: number) {
		if (conversionFactor === 1) {
			return
		}

		switch(this.getType()) {
			case PageType.EEG:
				const eegData = data as number[][]

				for (let i = 0; i < eegData.length; i++) {
					for (let j = 0; j < eegData[i].length; j++) {
						eegData[i][j] *= conversionFactor
					}
				}

				break
			default:
				const timeSeriesData = data as number[]

				for (let i = 0; i < data.length; i++) {
					timeSeriesData[i] *= conversionFactor
				}
		}
	}

	private combineTimeSeriesData(oldData: TraceData | undefined, newData: DecodedTimeSeriesData | undefined): TraceData {
		if (oldData === undefined) {
			oldData = { data: new Float32Array(), times: [] }
		}

		if (newData === undefined || newData.data.length === 0) {
			return oldData
		}

		const isComposite = Array.isArray(newData.data[0])

		if (oldData.data.length === 0) {
			if (isComposite) {
				return {
					times: newData.times,
					data: (newData.data as number[][]).map(column => this.mergeData(new Float32Array(), column as (number | null)[])),
				}
			}

			return {
				times: newData.times,
				data: this.mergeData(new Float32Array(), newData.data as (number | null)[]),
			}
		}

		const data: Float32Array | Float32Array[] = isComposite
			? (oldData.data as Float32Array[]).map((oldColumn, i) => this.mergeData(oldColumn, newData.data[i] as (number | null)[]))
			: this.mergeData(oldData.data as Float32Array, newData.data as (number | null)[])

		return { times: [...oldData.times, ...newData.times], data } as TraceData
	}

	private mergeData(oldColumn: Float32Array | undefined, newColumn: (number | null)[] | undefined): Float32Array {
		if (!oldColumn || !newColumn) {
			return new Float32Array()
		}

		const combinedArray = new Float32Array(oldColumn.length + newColumn.length)

		// Copy old data
		combinedArray.set(oldColumn)

		// Map new data, replacing null with NaN
		for (let j = 0; j < newColumn.length; j++) {
			combinedArray[oldColumn.length + j] = newColumn[j] === null ? NaN : newColumn[j]!
		}

		return combinedArray
	}

	public setSocketAccessor(getDataQuerySocket: null | ((id?: string) => Socket)) {
		this.getDataQuerySocket = getDataQuerySocket
	}

	// The maximum read time is the farthest that the page is allowed to read.
	// This is to prevent reading and caching analytics for data that doesn't exist yet.
	public updateMaxReadTime(newMaxReadTimeMilliseconds: number) {
		this.maxReadTime = newMaxReadTimeMilliseconds
	}

	private getSamplingPeriod(knownSamplingPeriod: number): number | null {
		const timeBetweenPixels = (this.endTime - this.startTime) / this.width
		return Math.max(knownSamplingPeriod, timeBetweenPixels)
	}

	private setUpDataStorage(response: SocketResponse) {
		if (!this.data.has(response.data_object_id)) {
			this.data.set(response.data_object_id, new Map())
		}

		if (!this.timingInformation.has(response.data_object_id)) {
			this.timingInformation.set(response.data_object_id, new Map())
		}

		const dataObjectDataMap = this.data.get(response.data_object_id)
		const timingInformationMap = this.timingInformation.get(response.data_object_id)
		
		if (!dataObjectDataMap || !timingInformationMap) {
			throw new Error("data object data map or timing information map was undefined.")
		}

		return { dataObjectDataMap, timingInformationMap }
	}

	private updateTimingInformation(timingInformationMap: Map<string, TimingInformation>, knownGaps: number[], knownSamplingPeriod: number, modality: string, traceData: TraceData) {
		const samplingPeriod = this.getSamplingPeriod(knownSamplingPeriod)
		const previousGaps = timingInformationMap.get(modality)?.gapIndexes ?? []
		let newGaps

		// Make sure we don't add the same gap indexes twice
		if (previousGaps.length === 0 || (previousGaps.length > 0 && knownGaps.length > 0 && previousGaps[0] === knownGaps[0]))  {
			newGaps = knownGaps
		} else {
			newGaps = [...previousGaps, ...knownGaps]
		}

		timingInformationMap.set(modality, {
			gapIndexes: newGaps,
			samplingPeriod,
		})
	}

	private updateLoadedState(response: SocketResponse): boolean {
		const loading = this.dataObjectsLoading.get(response.data_object_id) ?? new Map()
		const loaded = this.dataObjectsLoaded.get(response.data_object_id) ?? new Map()
		const responseDataSourceKey = this.getSocketResponseModalityDataSourceKey(response)
		const hasCompletedResponse = response.order === response.total

		this.updateReceivedDataState(response)
		this.dataObjectsLoading.set(response.data_object_id, loading.set(responseDataSourceKey, !hasCompletedResponse))
		this.dataObjectsLoaded.set(response.data_object_id, loaded.set(responseDataSourceKey, hasCompletedResponse && !this.hasNotReceivedFullDataRange))

		const allBuffersCompleted = [...this.responseBuffers.values()].every(buffer => buffer.isCompleted())

		if (allBuffersCompleted) {
			this.loadingMessage = "Loading Complete"
			this.onFinishedLoading?.(this)
		} else {
			this.loadingMessage = `Loaded ${response.modality} (${response.order}/${response.total})`
			this.onPartialLoad?.(this)
		}

		return allBuffersCompleted
	}

	private updateReceivedDataState(response: SocketResponse) {
		const fullRangeEndTime = this.patientIsAdmitted
			? this.endTime
			: Math.min(this.endTime, this.maxReadTime ?? this.endTime)

		this.hasNotReceivedFullDataRange = response.end_time < fullRangeEndTime

		if (response.order === response.total && this.hasNotReceivedFullDataRange) {
			this.lastRequestedPartialLoadEndTime = response.end_time
		}
	}

	public setOnPartialLoad = (onPartialLoad: (page: Page<any, any>) => void) => {
		this.onPartialLoad = onPartialLoad
	}

	public setOnFinishedLoading = (onFinishedLoading: (page: Page<any, any>) => void) => {
		this.onFinishedLoading = onFinishedLoading
	}
}
